import { useEffect, useState } from 'react';
import { ExternalLink, Linkedin, Send } from 'lucide-react';
import Popup from 'components/popup';
import { Button } from 'components/ui/button';
import type { LinkedInStatus } from 'services/api';
import type { CourseCompletionData } from 'types';

interface CertificatePreviewProps {
  learnerName: string;
  courseName: string;
  completedAt?: string | null;
  certificate?: CourseCompletionData['certificate'];
  linkedinStatus?: LinkedInStatus | null;
  linkedinStatusLoading?: boolean;
  linkedinPostState?: 'idle' | 'posting' | 'success' | 'error';
  linkedinPostUrl?: string | null;
  linkedinError?: string | null;
  linkedinErrorCode?: string | null;
  onConnectLinkedIn?: () => void;
  onPostLinkedIn?: (commentary: string) => void;
}

const formatCompletionDate = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const buildDefaultLinkedInPostText = (courseName: string, verifyUrl?: string) => {
  const text = `I completed ${courseName} on Wise Friend.`;

  return verifyUrl ? `${text}\n\nView completion: ${verifyUrl}` : text;
};

export function CertificatePreview({
  learnerName,
  courseName,
  completedAt,
  certificate,
  linkedinStatus,
  linkedinStatusLoading = false,
  linkedinPostState = 'idle',
  linkedinPostUrl,
  linkedinError,
  linkedinErrorCode,
  onConnectLinkedIn,
  onPostLinkedIn,
}: CertificatePreviewProps) {
  const [show, setShow] = useState(false);
  const issuedDate = formatCompletionDate(certificate?.issued_at ?? completedAt);
  const credentialId = certificate?.credential_id;
  const verifyDisplay = 'wisefriend.org/certificates';
  const defaultPostText = buildDefaultLinkedInPostText(courseName, certificate?.verify_url);
  const [postText, setPostText] = useState(defaultPostText);
  const [confirmPostOpen, setConfirmPostOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 1800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    setPostText(defaultPostText);
  }, [defaultPostText]);

  const hasPublishedLinkedInPost = linkedinPostState === 'success' && Boolean(linkedinPostUrl);
  const canUseNativeLinkedIn = Boolean(certificate && (linkedinStatus?.configured || hasPublishedLinkedInPost));
  const isConnected = Boolean(linkedinStatus?.connected);
  const isPostDisabled = linkedinPostState === 'posting' || !postText.trim();
  const canReconnectAfterPostFailure = linkedinPostState === 'error' && linkedinErrorCode === 'linkedin_missing_scope';

  const handleConfirmPost = () => {
    if (!postText.trim()) {
      return;
    }
    setConfirmPostOpen(false);
    onPostLinkedIn?.(postText);
  };

  return (
    <section aria-label="Certificate preview" className="px-4 pb-20 sm:px-6">
      <div
        className={`mx-auto max-w-[760px] transition-all duration-700 ease-out ${
          show ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-8 scale-[0.97]"
        }`}
      >
        <div className="flex items-center justify-center bg-[linear-gradient(135deg,rgb(var(--surface-sand-start))_0%,rgb(var(--surface-sand-end))_100%)]">
          <div className="relative w-full overflow-hidden bg-[#FFFDFB] shadow-[0_18px_45px_rgba(53,57,71,0.16)]">
            <div className="h-1.5 bg-foundation-orange-500" aria-hidden="true" />
            <div className="pointer-events-none absolute inset-4 border border-foundation-orange-100 sm:inset-5" aria-hidden="true" />

            <div className="relative px-6 py-7 sm:px-10 sm:py-9 lg:px-12 lg:py-10">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                <div className="flex items-center gap-3">
                  <img src="/assets/logo.svg" alt="" className="block h-auto w-[34px]" />
                  <span className="font-headings-h1 text-2xl text-foundation-base-darkbase-dark-700">Wise Friend</span>
                </div>
              </div>

              <div className="mt-10 text-center">
                <div className="text-[13px] font-semibold uppercase leading-none tracking-[0.32em] text-foundation-orange-500">
                  Certificate of Completion
                </div>
                <div className="mx-auto mt-[22px] h-0.5 w-12 bg-foundation-orange-300" aria-hidden="true" />

                <div className="mt-[34px] text-[15px] leading-[1.4] text-foundation-base-darkbase-dark-400">
                  This certifies that
                </div>

                <div className="mt-3.5 break-words font-headings-title text-4xl leading-[1.05] tracking-[-0.025em] text-foundation-base-darkbase-dark-700 sm:text-5xl lg:text-[60px]">
                  {learnerName}
                </div>

                <div className="mt-[18px] text-[15px] leading-[1.4] text-foundation-base-darkbase-dark-400">
                  has successfully completed
                </div>

                <div className="mx-auto mt-2.5 max-w-[680px] break-words font-headings-h1 text-2xl font-medium leading-[1.25] tracking-[-0.02em] text-foundation-base-darkbase-dark-700 [text-wrap:balance] lg:text-[30px]">
                  {courseName}
                </div>

                {issuedDate ? (
                  <div className="mt-[22px] text-sm leading-[1.4] text-foundation-base-darkbase-dark-300">
                    Issued on {issuedDate}
                  </div>
                ) : null}
              </div>

              <div className="mt-12 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0 text-left">
                  <img
                    src="/assets/wisefriend-signature.svg"
                    alt="Wise Friend"
                    className="mb-1 block h-[86px] w-[280px] max-w-full object-contain object-left"
                  />
                  <div className="h-px w-[280px] max-w-full bg-foundation-base-darkbase-dark-200" aria-hidden="true" />
                  <div className="mt-2 text-xs font-semibold leading-[1.3] text-foundation-base-darkbase-dark-700">
                    Wise Friend
                  </div>
                  <div className="text-xs leading-[1.3] text-foundation-base-darkbase-dark-400">Learning team</div>
                </div>

                <div className="flex flex-col items-center gap-2 self-center sm:self-auto">
                  <div className="relative flex h-[108px] w-[108px] items-center justify-center rounded-full border-2 border-foundation-orange-300">
                    <div className="absolute inset-[7px] rounded-full border border-dashed border-foundation-orange-100" aria-hidden="true" />
                    <div className="flex flex-col items-center gap-1">
                      <img src="/assets/logo.svg" alt="" className="block h-auto w-[38px]" />
                      <span className="text-[8px] font-semibold uppercase leading-none tracking-[0.16em] text-foundation-orange-500">
                        Verified
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-10 flex flex-col gap-5 border-t border-border pt-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                <div className="flex flex-col gap-[3px]">
                  <span className="text-[11px] font-semibold uppercase leading-none tracking-[0.14em] text-foundation-base-darkbase-dark-300">
                    Credential ID
                  </span>
                  {credentialId ? (
                    <span className="font-mono text-sm font-medium leading-[1.2] tracking-[0.02em] text-foundation-base-darkbase-dark-700">
                      {credentialId}
                    </span>
                  ) : (
                    <span className="text-sm font-medium leading-[1.2] text-foundation-base-darkbase-dark-700">
                      Certificate ready
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-[3px] text-left sm:text-right">
                  <span className="text-[11px] font-semibold uppercase leading-none tracking-[0.14em] text-foundation-base-darkbase-dark-300">
                    Verify certificate
                  </span>
                  {certificate?.verify_url ? (
                    <a
                      href={certificate.verify_url}
                      aria-label="Verify certificate"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="break-all font-mono text-sm font-medium leading-[1.2] text-foundation-orange-500 no-underline hover:underline"
                    >
                      {verifyDisplay}
                    </a>
                  ) : (
                    <span className="text-sm font-medium leading-[1.2] text-foundation-base-darkbase-dark-700">
                      Verified completion
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {canUseNativeLinkedIn ? (
          <div className="mt-5 space-y-4 rounded-lg border border-border bg-secondary/60 p-4">
            {hasPublishedLinkedInPost ? (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">Already posted to LinkedIn</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    This certificate has already been published from Wise Friend.
                  </p>
                </div>
                <a
                  href={linkedinPostUrl ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                >
                  <ExternalLink aria-hidden="true" className="h-4 w-4" />
                  View LinkedIn post
                </a>
              </div>
            ) : linkedinStatusLoading ? (
              <p className="text-sm text-muted-foreground">Checking LinkedIn connection...</p>
            ) : isConnected ? (
              <>
                <label className="block text-sm font-medium text-foreground" htmlFor="linkedin-post-text">
                  LinkedIn post text
                </label>
                <textarea
                  id="linkedin-post-text"
                  className="min-h-28 w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={postText}
                  maxLength={3000}
                  onChange={(event) => setPostText(event.target.value)}
                />
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-muted-foreground">{postText.length}/3000</p>
                  <Button
                    type="button"
                    size="auto"
                    className="w-full sm:w-auto px-6 py-3"
                    disabled={isPostDisabled}
                    onClick={() => setConfirmPostOpen(true)}
                  >
                    <Send aria-hidden="true" />
                    {linkedinPostState === 'posting' ? 'Posting...' : 'Post certificate to LinkedIn'}
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">Connect LinkedIn to post this certificate as a native image post.</p>
                <Button
                  type="button"
                  size="auto"
                  className="w-full sm:w-auto px-6 py-3"
                  onClick={onConnectLinkedIn}
                >
                  <Linkedin aria-hidden="true" />
                  Connect LinkedIn
                </Button>
              </div>
            )}
            {!hasPublishedLinkedInPost && linkedinPostState === 'error' && linkedinError ? (
              <div className="flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-destructive">{linkedinError}</p>
                {canReconnectAfterPostFailure ? (
                  <Button
                    type="button"
                    size="auto"
                    variant="secondary"
                    className="w-full sm:w-auto px-5 py-2"
                    onClick={onConnectLinkedIn}
                  >
                    <Linkedin aria-hidden="true" />
                    Reconnect LinkedIn
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {confirmPostOpen ? (
          <Popup
            onClose={() => setConfirmPostOpen(false)}
            title="Post certificate to LinkedIn?"
            description="This will publish the certificate image and post text to your LinkedIn profile."
            size="wide"
            contentLayout="form"
            actionsLayout="end"
            isSubmitting={linkedinPostState === 'posting'}
            actions={
              <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  size="auto"
                  variant="secondary"
                  className="px-5 py-2"
                  disabled={linkedinPostState === 'posting'}
                  onClick={() => setConfirmPostOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="auto"
                  className="px-5 py-2"
                  disabled={isPostDisabled}
                  onClick={handleConfirmPost}
                >
                  <Send aria-hidden="true" />
                  Publish to LinkedIn
                </Button>
              </div>
            }
          >
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Post text</p>
              <div className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-lg border border-border bg-background p-3 text-sm text-foreground">
                {postText}
              </div>
            </div>
          </Popup>
        ) : null}

      </div>
    </section>
  );
}
